import { useState } from 'react';
import Badge from '~/components/Badge';
import RadioGroup, {
  ButtonOption,
} from '~/components/form-controls/RadioGroup';
import { useCurrencyFormatter } from '~/utils/formatters';
import { FormKey } from './returnSummary';
import ReturnSummaryDisclosure from './ReturnSummaryDisclosure';

type Option = {
  label: string;
  type: string;
  description: string;
  amount?: number;
};

export default function Selection<T extends Option>({
  name,
  label,
  options,
  disabled = false,
  selected,
  onChange,
}: {
  name: FormKey;
  label: string;
  options: T[];
  disabled?: boolean;
  selected?: T | null;
  onChange: (value: T) => void;
}) {
  const [selectedState, setSelectedState] = useState(selected);
  const currencyFormatter = useCurrencyFormatter();

  if (options.length === 1) {
    return (
      <input
        data-testid="selection-input"
        type="hidden"
        name={name}
        value={selected?.type}
      />
    );
  }

  return (
    <ReturnSummaryDisclosure
      label="Selections"
      value={selectedState?.label ?? '1 Remaining'}
      defaultOpen
    >
      <RadioGroup label={label} showLabel={false} disabled={disabled}>
        <div className="flex flex-col gap-2">
          {options.map((o) => (
            <ButtonOption
              key={o.type}
              required
              name={name}
              value={o.type}
              checked={selectedState?.type === o.type}
              onChange={() => {
                setSelectedState(o);
                onChange(o);
              }}
              className="items-start rounded-lg! p-3 [&>input]:mt-0.5"
            >
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2 text-on-surface">
                  {o.label}
                  {o.amount && o.type === 'Gift_Card' && (
                    <>
                      {/* whitespace-only text is dropped by flex layout but keeps the accessible name readable */}{' '}
                      <Badge variant="success">{`${currencyFormatter(o.amount)} Incentive`}</Badge>
                    </>
                  )}
                </div>
                <p className="body-md text-on-surface-variant">
                  {o.description}
                </p>
              </div>
            </ButtonOption>
          ))}
        </div>
      </RadioGroup>
    </ReturnSummaryDisclosure>
  );
}
